<template>
  <v-card>
    <v-toolbar>
      <v-toolbar-title>{{ $i18n.translate("Claim Search") }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-form @submit.stop.prevent="onSubmit" class="d-flex align-center">
        <v-text-field
          class="mr-2"
          v-model="search"
          :label="$i18n.translate('Search by Claim ID, Customer Name/ID, or Email')"
          flat
          solo-inverted
          hide-details
          clearable
          clear-icon="mdi-close-circle-outline"
          style="min-width: 380px;"
          @click:clear="onClear"
        ></v-text-field>
        <v-btn type="submit" color="primary" :loading="loading">
          <v-icon left>mdi-magnify</v-icon>
          {{ $i18n.translate("Search") }}
        </v-btn>
      </v-form>
      <v-tooltip bottom v-if="search && search.length > 0">
        <template v-slot:activator="{ on: onTooltip }">
          <v-btn icon class="ml-2" v-on="{ ...onTooltip }" @click="onClearSearch">
            <v-icon>mdi-magnify-close</v-icon>
          </v-btn>
        </template>
        <span>{{ $i18n.translate("Clear Search") }}</span>
      </v-tooltip>
    </v-toolbar>
    <v-card-text>
      <v-container>
        <div>
          <v-data-table
            :items="items"
            :headers="headers"
            :server-items-length="total"
            :options.sync="options"
            :loading="loading"
            :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
            :no-data-text="$i18n.translate('No claims found matching your search.')"
          >
            <template v-slot:item.claimKey="{ item }">
              <div @contextmenu.prevent>
                <router-link :to="toClaimDetail(item)">
                  {{ item.claimKey }}
                </router-link>
              </div>
            </template>
            <template v-slot:item.participant.user.firstName="{ item }">
              {{ item.participant.user.fullName }}
            </template>
            <template v-slot:item.participant.user.email="{ item }">
              {{ item.participant.user.email }}
            </template>
            <template v-slot:item.calculatedTotalClaimAmount="{ item }">
              {{ item.calculatedCalculatedTotalClaimAmount | toNumber(2, selectedProgram) }}
              {{
                item.calculatedCalculatedTotalClaimAmountCurrency
                  ? item.calculatedCalculatedTotalClaimAmountCurrency
                  : ""
              }}
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip small :color="getStatusColor(item)" dark>
                {{ getStatusLabel(item) }}
              </v-chip>
            </template>
            <template v-slot:item.claimStage.name="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ item.claimStage | formatStage(item) }}
                  </span>
                </template>
                <span>{{ item.claimStage.description }}</span>
              </v-tooltip>
            </template>
            <template v-slot:item.updatedDate="{ item }">
              {{ item.updatedDate | formatDateFromNow }}
            </template>
            <template v-slot:item.createdDate="{ item }">
              {{ item.createdDate | formatDateFromNow }}
            </template>
          </v-data-table>
        </div>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import UtilService from "@/gapp-components/services/util.service";

export default {
  name: "ClaimSearch",
  metaInfo: {
    title: "Claim Search"
  },
  data() {
    return {
      search: "",
      loading: false,
      items: [],
      headers: [
        {
          value: "claimKey",
          text: "Claim ID",
          sortable: true
        },
        {
          value: "promotion.promotionType.name",
          text: "Type",
          sortable: true
        },
        {
          value: "promotion.name",
          text: "Promotion",
          sortable: true
        },
        {
          value: "participant.user.firstName",
          text: "Customer Name",
          sortable: true
        },
        {
          value: "participant.user.email",
          text: "Email",
          sortable: false
        },
        {
          value: "calculatedTotalClaimAmount",
          text: "Amount",
          align: "right",
          sortable: true
        },
        {
          value: "status",
          text: "Status",
          sortable: false
        },
        {
          value: "claimStage.name",
          text: "Stage",
          sortable: false
        },
        {
          value: "updatedDate",
          text: "Last Updated",
          sortable: true
        },
        {
          value: "createdDate",
          text: "Age",
          sortable: true
        }
      ],
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["updatedDate"],
        sortDesc: [true]
      }
    };
  },
  watch: {
    options: {
      handler() {
        this.getData();
      },
      deep: true
    }
  },
  methods: {
    onClear() {
      this.search = "";
      this.options.page = 1;
      this.getData();
    },
    onSubmit() {
      this.options.page = 1;
      this.getData();
    },
    onClearSearch() {
      this.search = "";
      this.options.page = 1;
      this.getData();
    },
    getData() {
      this.loading = true;

      const keyword = this.search ? this.search.trim() : "";
      const filters = {
        submitted: true
      };
      if (keyword.length > 0) {
        filters.keyword = keyword;
      }

      const params = UtilService.buildPaginationParams(this.options);
      this.$api
        .post(`/api/claims/search?${params.toString()}`, filters)
        .then(({ data }) => {
          this.loading = false;
          this.items = data.content;
          this.total = data.totalElements;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getStatusLabel(item) {
      if (item.closed) return "Closed";
      if (item.approved) return "Payment";
      return "Audit";
    },
    getStatusColor(item) {
      if (item.closed) return "grey";
      if (item.approved) return "green";
      return "blue";
    },
    toClaimDetail(item) {
      let params = { claim: item.id };
      const pageNumber = this.options.page || 1;

      if (this.search && this.search.length > 0) {
        params.keyword = this.search;
      }

      params.closed = item.closed;
      params.approved = item.approved;

      params.currentPosition = (pageNumber - 1) * this.options.itemsPerPage + this.items.indexOf(item);
      params.total = this.total;
      params.sortBy = this.options.sortBy[0];
      params.sortDesc = this.options.sortDesc[0];

      return { name: "cprClaimDetailManagement", query: params };
    }
  },
  mounted() {
    this.$store.dispatch("setBreadcrumb", [
      {
        text: this.$i18n.translate("Dashboard"),
        to: { name: "dashboard" },
        exact: true
      },
      { text: "Claim Search" }
    ]);
  },
  computed: {
    ...mapGetters(["selectedClient", "selectedProgram"])
  }
};
</script>
